(() => {
  if (customElements.get('product-share')) return;

  class ProductShare extends HTMLElement {
    connectedCallback() {
      if (this.button) return;
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-share-label]');
      this.status = this.querySelector('[data-share-status]');
      this.fallback = this.querySelector('.product-share__fallback');
      this.linkInput = this.fallback.querySelector('input');
      this.defaultLabel = this.label.textContent;
      this.button.hidden = false;
      this.button.addEventListener('click', () => this.share());
      this.linkInput.addEventListener('click', () => this.linkInput.select());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.fallback.hidden = true;
          this.button.focus();
        }
      });
    }

    getShareData() {
      const form = document.getElementById(this.dataset.formId);
      if (!form) return null;
      const picker = form.querySelector('product-variant-swatch, product-variant-dropdown');
      let variantId;
      let description = '';

      if (picker) {
        const options = picker.matches('product-variant-swatch')
          ? Array.from(picker.querySelectorAll('fieldset'), (fieldset) => fieldset.querySelector('input:checked')?.value)
          : Array.from(picker.querySelectorAll('select'), (select) => select.value);
        const variants = JSON.parse(picker.querySelector('[type="application/json"]').textContent);
        // Resolve the actual checked options, including immediately after a change.
        // The hidden cart input can still contain the previous variant for an invalid combination.
        const variant = variants.find((item) => item.options.length === options.length && item.options.every((value, index) => value === options[index]));
        if (!variant) return null;
        variantId = variant.id;
        if (variant.title !== 'Default Title') description = variant.title;
      } else {
        variantId = form.querySelector('[name="id"]')?.value;
      }

      if (!variantId) return null;
      // Start with the public product URL so theme-preview and tracking parameters never leak.
      const url = new URL(this.dataset.productUrl);
      url.searchParams.set('variant', variantId);
      return { title: this.dataset.productTitle, text: [this.dataset.productTitle, description].filter(Boolean).join(' — '), url: url.href };
    }

    announce(message, label = this.defaultLabel) {
      clearTimeout(this.resetTimer);
      this.status.textContent = message;
      this.label.textContent = label;
      this.resetTimer = setTimeout(() => { this.label.textContent = this.defaultLabel; }, 3000);
    }

    async copyLink(url) {
      try {
        await navigator.clipboard.writeText(url);
      } catch (_) {
        this.linkInput.value = url;
        this.fallback.hidden = false;
        this.linkInput.focus();
        this.linkInput.select();
        // Support browsers where the async Clipboard API is unavailable or denied.
        let copied = false;
        try {
          copied = document.execCommand('copy');
        } catch (_) {
          // The selected link remains available for manual copying.
        }
        if (!copied) {
          this.announce('Copy the link below to share your selected configuration.');
          return;
        }
        this.fallback.hidden = true;
        this.button.focus();
      }
      this.announce('Link copied with your selected configuration.', 'Link copied');
    }

    async share() {
      if (this.sharing) return;
      const data = this.getShareData();
      if (!data) {
        this.fallback.hidden = true;
        this.announce('Choose a valid product configuration before sharing.', 'Select options');
        return;
      }
      this.sharing = true;
      this.fallback.hidden = true;
      try {
        if (typeof navigator.share === 'function') {
          try {
            await navigator.share(data);
            this.announce('Selected configuration shared.');
            return;
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }
        await this.copyLink(data.url);
      } finally {
        this.sharing = false;
      }
    }

    disconnectedCallback() {
      clearTimeout(this.resetTimer);
    }
  }

  customElements.define('product-share', ProductShare);
})();
